import { ApiErrorSchema, type ApiError, type Problem } from "shared"

export class ApiResponseError extends Error {
  statusCode: number
  error: string
  code?: string
  /** Everything the server refused, when it listed them (ADR-033 rule 7). */
  problems: Problem[]

  constructor({ statusCode, error, message, code, problems }: ApiError) {
    super(message)
    this.name = "ApiResponseError"
    this.statusCode = statusCode
    this.error = error
    this.code = code
    this.problems = problems ?? []
  }
}

/**
 * A 4xx ApiResponseError — a business-rule rejection the server deliberately
 * returned (duplicate feedback, closed session, rate limit). These are expected
 * outcomes we surface to the user, not failures worth logging as exceptions.
 * 5xx / network / parse errors are not "expected" by this test.
 */
export function isExpectedApiError(err: unknown): err is ApiResponseError {
  return err instanceof ApiResponseError && err.statusCode < 500
}

/**
 * A 403 from the API — the caller is authenticated but their role isn't
 * permitted here (the service_admin gate on the admin routes). Pages use this to
 * show a "not authorized" message instead of a generic failure.
 */
export function isForbidden(err: unknown): err is ApiResponseError {
  return err instanceof ApiResponseError && err.statusCode === 403
}

/**
 * A 401 from the API — the caller holds no valid credential. On the kiosk that
 * means the device credential is absent or revoked (ADR-025), and the tablet is
 * returned to setup. Kiosk pages get this via useKioskQuery and
 * useCreateInvitation rather than testing it themselves.
 */
export function isUnauthorized(err: unknown): err is ApiResponseError {
  return err instanceof ApiResponseError && err.statusCode === 401
}

/**
 * A 404 from the API — the caller is authenticated but the resource is gone.
 */
export function isNotFound(err: unknown): err is ApiResponseError {
  return err instanceof ApiResponseError && err.statusCode === 404
}

export async function parseApiError(res: Response): Promise<ApiResponseError> {
  try {
    return new ApiResponseError(ApiErrorSchema.parse(await res.json()))
  } catch {
    return new ApiResponseError({
      statusCode: res.status,
      error: res.statusText,
      message: "An unexpected error occurred",
    })
  }
}
